/**
 * Lane CQ-238 (operator 2026-10-13, Feedback › Citation problems — the Document cell showed a raw file name wrapping over three lines:
 * "Put the link of these documents in here if possible") — the ONE href/label decision for every console cell that names a knowledge
 * DOCUMENT.  Pure; drawn by DocumentLink.jsx, which every such cell uses (documentLink.census.cq238.test.jsx holds them to it).
 *
 *   label     the document's title in words: a title the row carries, else its file name read as words (documentTitleOf) — never
 *             the raw file name when a title exists.
 *   fileName  the raw file name, only when it says more than the label — the cell shows it under Technical details only.
 *   href      the address that opens the document: only an absolute https address (the knowledge source's own SharePoint / web
 *             page, or the media host's stored rendition) — never a bare file name, a blob locator, http or a script URL; null ⇒
 *             the cell draws the plain title (nothing broken).
 *   atPage    the address opens the document AT the cited page (documentHref): a PDF or the media host's rendition takes #page=N,
 *             the media host's /view page takes ?page=N; any other address (a Word file in SharePoint, a web page) opens the
 *             document itself and atPage stays false (the cell never claims a page it cannot open).
 */

/** The file kinds a knowledge DOCUMENT is named by (the knowledge index names a document by its file name) — the extension the title
 *  in words drops, and what tells a document's name from a page title (isKnowledgeDocument).  An explicit list: "Release 2.1" keeps
 *  its ".1". */
const DOCUMENT_EXTENSIONS = ['pdf', 'doc', 'docx', 'docm', 'rtf', 'odt', 'txt', 'md', 'xls', 'xlsx', 'xlsm', 'csv', 'ods', 'ppt',
  'pptx', 'odp', 'png', 'jpg', 'jpeg', 'gif', 'tif', 'tiff', 'mp4', 'mov', 'mp3', 'wav', 'm4a', 'msg', 'eml', 'vsdx', 'json', 'xml'];
const DOCUMENT_EXTENSION = new RegExp(`\\.(${DOCUMENT_EXTENSIONS.join('|')})$`, 'i');
const PAGE_EXTENSION = /\.html?$/i;                      // a saved web page's name — dropped from the words, never a document
const MEDIA_TOKEN = /^\/m\/[^/]+(\/[^/]+)?$/;            // /m/{token}[/{name}]  — the media host's signed link
const MEDIA_SHORT = /^\/k\/[^/]+\/[A-Za-z0-9_-]{22}\/[^/]+$/; // /k/{tenant}/{key}/{file} — the media host's short link
const MEDIA_VIEW = /^\/view\//;                          // /view/{token}?title=&page= — the media host's viewer page

/** The file name in words — never a raw file name: a folder path and a document extension dropped, `%20` decoded, underscores read as
 *  spaces, a hyphenated slug ("supplier-onboarding-guide") read as words, the first letter capitalised; a title already in words stays
 *  as written; a name with nothing left but its extension stays as it is.  Null when there is no name.  Pure. */
export function documentTitleOf(name) {
  if (typeof name !== 'string') return null;
  const raw = name.trim();
  if (!raw) return null;
  let s = raw.split(/[\\/]/).pop();
  if (/%[0-9A-Fa-f]{2}/.test(s)) { try { s = decodeURIComponent(s); } catch { /* a stray % — keep the name as written */ } }
  s = s.replace(DOCUMENT_EXTENSION, '').replace(PAGE_EXTENSION, '');
  s = s.replace(/[_+]+/g, ' ');
  if (!/\s/.test(s) && s.includes('-')) s = s.replace(/-+/g, ' ');
  s = s.replace(/\s+/g, ' ').trim();
  return s ? s.charAt(0).toLocaleUpperCase('en') + s.slice(1) : raw;
}

/** The address that opens the document at `page` where it can carry one: `{ href, atPage }`.  Only an absolute https URL is an href
 *  (else `{ href: null, atPage: false }`).  Pure. */
export function documentHref(url, page) {
  const none = { href: null, atPage: false };
  if (typeof url !== 'string' || !url.trim()) return none;
  let u;
  try { u = new URL(url.trim()); } catch { return none; }
  if (u.protocol !== 'https:' || !u.hostname) return none;
  const href = u.toString();
  if (!Number.isInteger(page) || page < 1) return { href, atPage: false };
  const path = u.pathname;
  if (MEDIA_VIEW.test(path)) {
    u.searchParams.set('page', String(page));
    return { href: u.toString(), atPage: true };
  }
  if (/\.pdf$/i.test(path) || MEDIA_TOKEN.test(path) || MEDIA_SHORT.test(path)) {
    u.hash = `page=${page}`;
    return { href: u.toString(), atPage: true };
  }
  return { href, atPage: false };
}

/** The kinds that are web pages, not documents (KnowledgeMediaType.WebPage; a web_search reference's "web"). */
const WEB_KINDS = new Set(['webpage', 'web']);

/** Is this source a knowledge DOCUMENT (drawn by DocumentLink) rather than a web page or a business tool (each keeps its own
 *  rendering)?  `kind` = the knowledge index's media type when the row carries one (a story reference's `kind`, a manifest item's
 *  `mediaType`): `WebPage` / `web` ⇒ no, any other kind (Document, Spreadsheet, Presentation, Image, Video, Audio, Other, pdf) ⇒ yes;
 *  no kind ⇒ the name decides (a document file name ⇒ yes).  Pure. */
export function isKnowledgeDocument({ kind, title } = {}) {
  const k = typeof kind === 'string' ? kind.trim().toLowerCase() : '';
  if (k) return !WEB_KINDS.has(k);
  return typeof title === 'string' && DOCUMENT_EXTENSION.test(title.trim());
}

/** What a document cell draws: `{ label, href, atPage, page, fileName }` (see the file header).  `title` = a human title the row
 *  carries (rare — the knowledge index names documents by file name), `fileName` = the file name, `url` = the address the server
 *  resolved, `page` = the cited page.  Pure. */
export function documentLinkOf({ title, fileName, url, page } = {}) {
  const name = typeof fileName === 'string' && fileName.trim() ? fileName.trim() : null;
  const human = typeof title === 'string' && title.trim() ? title.trim() : null;
  const label = human || documentTitleOf(name);
  const { href, atPage } = label ? documentHref(url, page) : { href: null, atPage: false };
  return {
    label: label || null,
    href,
    atPage,
    page: Number.isInteger(page) && page >= 1 ? page : null,
    fileName: name && name !== label ? name : null,
  };
}
